import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { WaveformBars } from '../../atoms/waveform-bars/waveform-bars';
import { AudioTranscodeService } from '../../../services/audio-transcode.service';

/** State machine for the recorder flow. */
const RecorderState = {
  IDLE: 'idle',
  RECORDING: 'recording',
  RECORDED: 'recorded',
  PLAYING: 'playing',
  PAUSED: 'paused',
} as const;

type RecorderState = (typeof RecorderState)[keyof typeof RecorderState];

@Component({
  selector: 'om-audio-recorder',
  imports: [CommonModule, WaveformBars],
  templateUrl: './audio-recorder.html',
  styleUrl: './audio-recorder.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AudioRecorder implements AfterViewInit, OnDestroy {
  private readonly transcodeService = inject(AudioTranscodeService);

  /** Optional title displayed above the waveform. */
  title = input<string>('');

  /** Emitted when audio is ready (recorded or uploaded). */
  audioReady = output<Blob>();

  /** Emitted when the user clears the audio. */
  audioCleared = output<void>();

  /** Canvas element reference for waveform drawing. */
  readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('waveCanvas');

  /** Current state of the recorder. */
  readonly state = signal<RecorderState>(RecorderState.IDLE);

  /** Duration of the current recording/playback in seconds. */
  readonly durationSeconds = signal(0);

  /** Current playback position in seconds. */
  readonly currentTime = signal(0);

  /** Whether an audio file has been loaded (recorded or uploaded). */
  readonly hasAudio = computed(() =>
    this.state() !== RecorderState.IDLE
  );

  /** Formatted total duration string (m:ss). */
  readonly formattedDuration = computed(() =>
    this.formatTime(this.durationSeconds())
  );

  /** Formatted current time string (m:ss). */
  readonly formattedCurrentTime = computed(() =>
    this.formatTime(this.currentTime())
  );

  /* ── Private internals ───────────────────────────── */
  private mediaRecorder: MediaRecorder | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private mediaStream: MediaStream | null = null;
  private audioChunks: Blob[] = [];
  private audioBlob: Blob | null = null;
  private audioElement: HTMLAudioElement | null = null;
  private animationFrameId = 0;
  private durationInterval: ReturnType<typeof setInterval> | null = null;
  private waveformData: number[] = [];
  /** Accumulated amplitude samples during recording (one per frame) — grows left to right. */
  private liveWaveformSamples: number[] = [];
  private resizeObserver: ResizeObserver | null = null;

  private static readonly BAR_COUNT = 130;
  /** Max samples to keep (avoids unbounded growth); older samples drop. */
  private static readonly MAX_LIVE_SAMPLES = 600;

  ngAfterViewInit(): void {
    this.setupResizeObserver();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  /* ── Public actions ──────────────────────────────── */

  /** Starts microphone recording. */
  async startRecording(): Promise<void> {
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 512;
      this.analyser.smoothingTimeConstant = 0.7;

      const source = this.audioContext.createMediaStreamSource(this.mediaStream);
      source.connect(this.analyser);

      const mimeType = this.transcodeService.getRecorderMimeType();
      this.mediaRecorder = mimeType
        ? new MediaRecorder(this.mediaStream, { mimeType })
        : new MediaRecorder(this.mediaStream);
      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) this.audioChunks.push(e.data);
      };

      this.mediaRecorder.onstop = () => {
        const recordedMime = this.mediaRecorder?.mimeType || mimeType || 'audio/webm';
        this.audioBlob = new Blob(this.audioChunks, { type: recordedMime });
        this.state.set(RecorderState.RECORDED);
        this.audioReady.emit(this.audioBlob);
        this.preparePlayback(this.audioBlob);
        this.stopLiveWaveform();
      };

      this.mediaRecorder.start();
      this.state.set(RecorderState.RECORDING);
      this.durationSeconds.set(0);
      this.liveWaveformSamples = [];
      this.startDurationTimer();
      // Defer so Angular renders the recording template (with its canvas) before we start drawing
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.drawLiveWaveform());
      });
      this.setupResizeObserver();
    } catch {
      console.error('Microphone access denied');
    }
  }

  /** Stops the current recording. */
  stopRecording(): void {
    if (this.mediaRecorder?.state === 'recording') {
      this.mediaRecorder.stop();
      this.stopDurationTimer();
      this.stopMediaStream();
    }
  }

  /** Plays the recorded/uploaded audio. */
  play(): void {
    if (!this.audioElement) return;
    this.audioElement.play();
    this.state.set(RecorderState.PLAYING);
    this.drawPlaybackWaveform();
  }

  /** Pauses playback. */
  pause(): void {
    if (!this.audioElement) return;
    this.audioElement.pause();
    this.state.set(RecorderState.PAUSED);
    cancelAnimationFrame(this.animationFrameId);
  }

  /** Handles file upload from the user. */
  async onFileUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    this.audioBlob = file;
    this.state.set(RecorderState.RECORDED);
    this.audioReady.emit(this.audioBlob);
    await this.preparePlayback(this.audioBlob);
    await this.generateStaticWaveform(this.audioBlob);
  }

  /** Triggers the hidden file input. */
  triggerUpload(): void {
    const input = document.getElementById('audioFileInput') as HTMLInputElement;
    input?.click();
  }

  /** Clears the current audio and resets to idle. */
  clearAudio(): void {
    this.cleanup();
    this.audioBlob = null;
    this.audioChunks = [];
    this.waveformData = [];
    this.liveWaveformSamples = [];
    this.durationSeconds.set(0);
    this.currentTime.set(0);
    this.state.set(RecorderState.IDLE);
    this.audioCleared.emit();
  }

  /* ── Private helpers ─────────────────────────────── */

  /** Smoothstep easing: maps 0–1 to smooth s-curve. */
  private smoothstep(x: number): number {
    const t = Math.max(0, Math.min(1, x));
    return t * t * (3 - 2 * t);
  }

  /** Setup ResizeObserver for responsive canvas. */
  private setupResizeObserver(): void {
    this.resizeObserver?.disconnect();
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas) return;
    this.resizeObserver = new ResizeObserver(() => this.redrawCurrentState());
    this.resizeObserver.observe(canvas);
  }

  /** Redraw based on current state (used on resize). */
  private redrawCurrentState(): void {
    const s = this.state();
    if (s === RecorderState.RECORDING) {
      this.drawLiveWaveform();
    } else if (this.waveformData.length > 0) {
      if (s === RecorderState.PLAYING) {
        this.drawPlaybackWaveform();
      } else {
        this.drawStaticWaveform();
      }
    }
  }

  /** Formats seconds to m:ss display string. */
  private formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  /** Sets up the HTMLAudioElement for playback. */
  private async preparePlayback(blob: Blob): Promise<void> {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.removeAttribute('src');
    }

    this.audioElement = new Audio(URL.createObjectURL(blob));

    this.audioElement.onloadedmetadata = () => {
      if (this.audioElement && isFinite(this.audioElement.duration)) {
        this.durationSeconds.set(this.audioElement.duration);
      }
    };

    this.audioElement.ontimeupdate = () => {
      if (this.audioElement) {
        this.currentTime.set(this.audioElement.currentTime);
      }
    };

    this.audioElement.onended = () => {
      this.state.set(RecorderState.RECORDED);
      this.currentTime.set(0);
      cancelAnimationFrame(this.animationFrameId);
      this.drawStaticWaveform();
    };
  }

  /** Generates a static waveform from decoded audio data (smooth, more bars). */
  private async generateStaticWaveform(blob: Blob): Promise<void> {
    try {
      const ctx = new AudioContext();
      const buffer = await blob.arrayBuffer();
      const audioBuffer = await ctx.decodeAudioData(buffer);
      const channelData = audioBuffer.getChannelData(0);

      const barCount = AudioRecorder.BAR_COUNT;
      const samplesPerBar = Math.floor(channelData.length / barCount);
      const raw: number[] = [];

      for (let i = 0; i < barCount; i++) {
        let sum = 0;
        for (let j = 0; j < samplesPerBar; j++) {
          sum += Math.abs(channelData[i * samplesPerBar + j]);
        }
        raw.push(sum / samplesPerBar);
      }

      // Apply smoothing (3-point moving average)
      const smoothed = raw.map((v, i) => {
        const prev = raw[Math.max(0, i - 1)];
        const next = raw[Math.min(barCount - 1, i + 1)];
        return (prev + v * 2 + next) / 4;
      });
      const max = Math.max(...smoothed, 0.01);
      this.waveformData = smoothed.map((v) => this.smoothstep(v / max));

      this.drawStaticWaveform();
      await ctx.close();
    } catch {
      // generateStaticWaveform failed — idle state handled by WaveformBars atom
    }
  }

  /* ── Canvas drawing ──────────────────────────────── */

  /** Draws a live waveform during recording — accumulates over time, advances left to right. */
  private drawLiveWaveform(): void {
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas || !this.analyser) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(2, window.devicePixelRatio ?? 1);
    const timeDataLength = this.analyser.fftSize;
    const timeDataArray = new Uint8Array(timeDataLength);

    const draw = () => {
      if (this.state() !== RecorderState.RECORDING) return;
      this.animationFrameId = requestAnimationFrame(draw);

      // Use time-domain data (more responsive for voice) + gain for visibility
      this.analyser?.getByteTimeDomainData(timeDataArray);
      let sum = 0;
      for (let i = 0; i < timeDataLength; i++) {
        const v = (timeDataArray[i] - 128) / 128;
        sum += Math.abs(v);
      }
      const rawAmplitude = sum / timeDataLength;
      const gain = 6;
      const amplitude = Math.min(1, rawAmplitude * gain);
      this.liveWaveformSamples.push(this.smoothstep(amplitude));
      if (this.liveWaveformSamples.length > AudioRecorder.MAX_LIVE_SAMPLES) {
        this.liveWaveformSamples.shift();
      }

      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barCount = AudioRecorder.BAR_COUNT;
      const barWidth = (canvas.width / barCount) * 0.6;
      const gap = (canvas.width / barCount) * 0.4;
      const centerY = canvas.height / 2;
      const sampleCount = this.liveWaveformSamples.length;

      ctx.shadowColor = 'rgba(224, 122, 95, 0.4)';
      ctx.shadowBlur = 12;

      // Draw: left = older, right = newest. Waveform advances as you record.
      for (let i = 0; i < barCount; i++) {
        let value: number;
        if (sampleCount <= barCount) {
          value = i < sampleCount ? this.liveWaveformSamples[i] : 0;
        } else {
          const sampleIndex = sampleCount - barCount + i;
          value = this.liveWaveformSamples[sampleIndex] ?? 0;
        }
        const h = Math.max(6, value * centerY * 0.85);
        const x = i * (barWidth + gap);
        const t = i / barCount;

        const r = 225 - t * 30;
        const g = 124 + t * 40;
        const b = 96 + t * 30;
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${0.5 + value * 0.5})`;
        ctx.beginPath();
        ctx.roundRect(x, centerY - h, barWidth, h * 2, barWidth / 2);
        ctx.fill();
      }

      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    };

    draw();
  }

  /** Draws the static waveform from decoded audio data. */
  private drawStaticWaveform(): void {
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas || this.waveformData.length === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(2, window.devicePixelRatio ?? 1);
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const barCount = this.waveformData.length;
    const barWidth = (canvas.width / barCount) * 0.6;
    const gap = (canvas.width / barCount) * 0.4;
    const centerY = canvas.height / 2;

    for (let i = 0; i < barCount; i++) {
      const value = this.waveformData[i];
      const h = Math.max(6, value * centerY * 0.85);
      const x = i * (barWidth + gap);
      const t = i / barCount;

      const r = 225 - t * 30;
      const g = 124 + t * 40;
      const b = 96 + t * 30;
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${0.45 + value * 0.55})`;
      ctx.beginPath();
      ctx.roundRect(x, centerY - h, barWidth, h * 2, barWidth / 2);
      ctx.fill();
    }
  }

  /** Draws animated playback waveform with progress indicator and glow. */
  private drawPlaybackWaveform(): void {
    const canvas = this.canvasRef()?.nativeElement;
    if (!canvas || this.waveformData.length === 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(2, window.devicePixelRatio ?? 1);

    const draw = () => {
      if (this.state() !== RecorderState.PLAYING) return;
      this.animationFrameId = requestAnimationFrame(draw);

      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barCount = this.waveformData.length;
      const barWidth = (canvas.width / barCount) * 0.6;
      const gap = (canvas.width / barCount) * 0.4;
      const centerY = canvas.height / 2;
      const progress = this.audioElement
        ? this.audioElement.currentTime / (this.audioElement.duration || 1)
        : 0;

      ctx.shadowColor = 'rgba(224, 122, 95, 0.25)';
      ctx.shadowBlur = 6;

      for (let i = 0; i < barCount; i++) {
        const value = this.waveformData[i];
        const h = Math.max(6, value * centerY * 0.85);
        const x = i * (barWidth + gap);
        const barProgress = i / barCount;
        const t = i / barCount;

        const r = 225 - t * 30;
        const g = 124 + t * 40;
        const b = 96 + t * 30;

        if (barProgress <= progress) {
          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${0.55 + value * 0.45})`;
        } else {
          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.18)`;
        }

        ctx.beginPath();
        ctx.roundRect(x, centerY - h, barWidth, h * 2, barWidth / 2);
        ctx.fill();
      }

      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    };

    draw();
  }

  /* ── Timers & cleanup ────────────────────────────── */

  private startDurationTimer(): void {
    this.durationInterval = setInterval(() => {
      this.durationSeconds.update((d) => d + 1);
    }, 1000);
  }

  private stopDurationTimer(): void {
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }
  }

  private stopLiveWaveform(): void {
    cancelAnimationFrame(this.animationFrameId);
    if (this.audioBlob) {
      this.generateStaticWaveform(this.audioBlob);
    }
  }

  private stopMediaStream(): void {
    this.mediaStream?.getTracks().forEach((t) => t.stop());
    this.mediaStream = null;
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(() => {
        // Ignore — context may have been closed concurrently (tab suspend, double cleanup race)
      });
    }
    this.audioContext = null;
    this.analyser = null;
  }

  private cleanup(): void {
    this.stopDurationTimer();
    cancelAnimationFrame(this.animationFrameId);
    this.stopMediaStream();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement = null;
    }
  }
}
